import React, { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash, 
  Eye, 
  Filter, 
  ChevronRight, 
  ChevronDown, 
  Link as LinkIcon,
  FolderPlus,
  X,
  Copy,
  Loader2,
  Send
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { formatSafeDate } from "@/lib/date-utils";
import ErrorBoundary from '@/components/ErrorBoundary';
import { Textarea } from '@/components/ui/textarea';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

// Importar serviços do Supabase
import { tripService, Trip as SupabaseTrip } from '@/lib/supabase/trips';
import { quotationService, Quotation as SupabaseQuotation } from '@/lib/supabase/quotations';
import { clientService, Client as SupabaseClient } from '@/lib/supabase/clients';

// Adicionar o serviço de versionamento
import { quotationVersioningService } from '@/services/quotationVersioningService';
import { QuotationVersion } from '@/components/quotation/PublishButton';

// Lista de países
const countries = [
  "Afeganistão", "África do Sul", "Albânia", "Alemanha", "Andorra", "Angola", "Antígua e Barbuda", "Arábia Saudita", 
  "Argélia", "Argentina", "Armênia", "Austrália", "Áustria", "Azerbaijão", "Bahamas", "Bangladesh", "Barbados", 
  "Barein", "Bélgica", "Belize", "Benin", "Bielorrússia", "Bolívia", "Bósnia e Herzegovina", "Botsuana", "Brasil", 
  "Brunei", "Bulgária", "Burkina Faso", "Burundi", "Butão", "Cabo Verde", "Camarões", "Camboja", "Canadá", "Catar", 
  "Cazaquistão", "Chade", "Chile", "China", "Chipre", "Colômbia", "Comores", "Congo", "Coreia do Norte", "Coreia do Sul", 
  "Costa do Marfim", "Costa Rica", "Croácia", "Cuba", "Dinamarca", "Djibouti", "Dominica", "Egito", "El Salvador", 
  "Emirados Árabes Unidos", "Equador", "Eritreia", "Eslováquia", "Eslovênia", "Espanha", "Estados Unidos", "Estônia", 
  "Etiópia", "Fiji", "Filipinas", "Finlândia", "França", "Gabão", "Gâmbia", "Gana", "Geórgia", "Granada", "Grécia", 
  "Guatemala", "Guiana", "Guiné", "Guiné-Bissau", "Guiné Equatorial", "Haiti", "Holanda", "Honduras", "Hungria", "Iêmen", 
  "Ilhas Marshall", "Ilhas Maurício", "Ilhas Salomão", "Índia", "Indonésia", "Irã", "Iraque", "Irlanda", "Islândia", 
  "Israel", "Itália", "Jamaica", "Japão", "Jordânia", "Kiribati", "Kuwait", "Laos", "Lesoto", "Letônia", "Líbano", 
  "Libéria", "Líbia", "Liechtenstein", "Lituânia", "Luxemburgo", "Macedônia do Norte", "Madagascar", "Malásia", "Malaui", 
  "Maldivas", "Mali", "Malta", "Marrocos", "Mauritânia", "México", "Micronésia", "Moçambique", "Moldávia", "Mônaco", 
  "Mongólia", "Montenegro", "Myanmar", "Namíbia", "Nauru", "Nepal", "Nicarágua", "Níger", "Nigéria", "Noruega", 
  "Nova Zelândia", "Omã", "Palau", "Panamá", "Papua-Nova Guiné", "Paquistão", "Paraguai", "Peru", "Polônia", "Portugal", 
  "Quênia", "Quirguistão", "Reino Unido", "República Centro-Africana", "República Checa", "República Democrática do Congo", 
  "República Dominicana", "Romênia", "Ruanda", "Rússia", "Samoa", "San Marino", "Santa Lúcia", "São Cristóvão e Névis", 
  "São Tomé e Príncipe", "São Vicente e Granadinas", "Seychelles", "Senegal", "Serra Leoa", "Sérvia", "Singapura", "Síria", 
  "Somália", "Sri Lanka", "Suazilândia", "Sudão", "Sudão do Sul", "Suécia", "Suíça", "Suriname", "Tailândia", "Taiwan", 
  "Tajiquistão", "Tanzânia", "Timor-Leste", "Togo", "Tonga", "Trinidad e Tobago", "Tunísia", "Turcomenistão", "Turquia", 
  "Tuvalu", "Ucrânia", "Uganda", "Uruguai", "Uzbequistão", "Vanuatu", "Vaticano", "Venezuela", "Vietnã", "Zâmbia", "Zimbábue"
];

// Use the imported Trip interface instead of defining a local one
type Trip = SupabaseTrip;

interface Quotation {
  id: string;
  title?: string;
  client: string;
  destination: string[] | string;
  startDate?: Date | null;
  endDate?: Date | null;
  status: string;
  tripId?: string;
  showRecommendationsTab?: boolean;
  showPriceTab?: boolean;
  showAccommodationsTab?: boolean;
  accommodationsTabName?: string;
  showFlightsTab?: boolean;
  flightsTabName?: string;
  priceTabName?: string;
  recommendationsTabName?: string;
  useDayMode?: boolean;
  usePageDropdown?: boolean;
  showMapLines?: boolean;
  showAccommodationsInItinerary?: boolean;
  showFlightsInItinerary?: boolean;
  useCustomAccommodationGroups?: boolean;
  accommodationsDescription?: string;
  useCustomFlightGroups?: boolean;
  flightsDescription?: string;
  showAccommodationsMapSection?: boolean;
  accommodationGroups?: any[];
  flightGroups?: any[];
  recommendationGroups?: any[];
}

interface Client {
  id: string;
  name: string;
  email?: string;
  phone: string;
  notes: string;
}

export default function TravelHub() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [trips, setTrips] = useState<Trip[]>([]);
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [expandedTrips, setExpandedTrips] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState<'all' | 'trips-only' | 'quotations-only'>('all');
  const [isLoading, setIsLoading] = useState(false);
  
  // Adicionar novos estados para o formulário de viagem
  const [showTripForm, setShowTripForm] = useState(false);
  const [newTrip, setNewTrip] = useState<Partial<Trip>>({
    destination: [],
    clientIds: [],
    status: 'quotation',
  });
  
  // Estados para os campos de destino
  const [destinationInputOpen, setDestinationInputOpen] = useState(false);
  const [searchDestination, setSearchDestination] = useState('');
  
  // Estados para busca de clientes
  const [clientSearch, setClientSearch] = useState('');
  const [availableClients, setAvailableClients] = useState<Client[]>([]);
  const [selectedClients, setSelectedClients] = useState<Client[]>([]);
  
  // Mapeamento de IDs de cliente para nomes, para cache
  const [clientNamesMap, setClientNamesMap] = useState<Record<string, string>>({});
  
  // Adicionar novos estados para o diálogo de versões
  const [showVersionsDialog, setShowVersionsDialog] = useState(false);
  const [selectedQuotationId, setSelectedQuotationId] = useState<string | null>(null);
  const [workVersion, setWorkVersion] = useState<QuotationVersion | null>(null);
  const [publishedVersion, setPublishedVersion] = useState<QuotationVersion | null>(null);
  const [historyVersion, setHistoryVersion] = useState<QuotationVersion | null>(null);
  const [copiedVersionId, setCopiedVersionId] = useState<string | null>(null);
  
  // Obter cache de nomes de clientes para exibição rápida
  const getDisplayClientNames = (clientIds: string[] = []) => {
    if (!clientIds.length) return 'Sem cliente';
    
    const availableNames = clientIds
      .map(id => clientNamesMap[id])
      .filter(Boolean);
    
    if (availableNames.length === 0) {
      // Programar carregamento assíncrono dos nomes
      loadClientNames(clientIds);
      return 'Carregando...';
    }
    
    if (availableNames.length === 1) return availableNames[0];
    if (availableNames.length === 2) return `${availableNames[0]} e ${availableNames[1]}`;
    
    return `${availableNames[0]}, ${availableNames[1]} +${availableNames.length - 2}`;
  };
  
  // Função para carregar nomes de clientes assincronamente
  const loadClientNames = async (clientIds: string[]) => {
    try {
      const missingIds = clientIds.filter(id => !clientNamesMap[id]);
      if (missingIds.length === 0) return;
      
      // Carregar detalhes dos clientes em paralelo
      const clientPromises = missingIds.map(id => clientService.getClientById(id));
      const clients = await Promise.all(clientPromises);
      
      // Atualizar o mapa de nomes
      const newEntries = clients.reduce((acc, client) => {
        if (client) {
          acc[client.id] = client.name;
        }
        return acc;
      }, {} as Record<string, string>);
      
      setClientNamesMap(prev => ({...prev, ...newEntries}));
      } catch (error) {
      console.error('Error loading client names:', error);
    }
  };
  
  // Carregar clientes iniciais quando o componente montar
  useEffect(() => {
    const loadInitialClients = async () => {
      try {
        const clients = await clientService.getClients();
        const nameMap = clients.reduce((acc, client) => {
          acc[client.id] = client.name;
          return acc;
        }, {} as Record<string, string>);
        
        setClientNamesMap(nameMap);
      } catch (error) {
        console.error('Error loading initial clients:', error);
      }
    };
    
    loadInitialClients();
  }, []);

  // Carregar viagens e cotações do Supabase
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // Carregar viagens do Supabase
        const tripsData = await tripService.getTrips();
        
        // Converter formato do Supabase para o formato do componente
        const formattedTrips: Trip[] = tripsData.map(trip => ({
          id: trip.id,
          title: trip.title,
          clientIds: trip.client_ids || [],
          destination: trip.destination,
          startDate: trip.start_date ? trip.start_date.split('T')[0] : '',
          endDate: trip.end_date ? trip.end_date.split('T')[0] : '',
          status: trip.status,
          notes: trip.notes,
          coverImages: trip.cover_images,
          days: trip.days,
          itineraryType: trip.itinerary_type
        }));
        
        setTrips(formattedTrips);
        
        // Carregar cotações do Supabase
        const quotationsData = await quotationService.getQuotations();
        
        // Converter formato do Supabase para o formato do componente
        const formattedQuotations: Quotation[] = quotationsData.map(quotation => ({
          id: quotation.id,
          title: quotation.title,
          client: quotation.client || '',
          destination: quotation.destination,
          startDate: quotation.start_date ? new Date(quotation.start_date) : null,
          endDate: quotation.end_date ? new Date(quotation.end_date) : null,
          status: 'quotation', // Status padrão para cotações
          tripId: quotation.trip_id,
          showRecommendationsTab: quotation.show_recommendations_tab,
          showPriceTab: quotation.show_price_tab
        }));
        
        setQuotations(formattedQuotations);
        
      } catch (error) {
        console.error('Error loading data from Supabase:', error);
        toast({
          title: "Erro",
          description: "Não foi possível carregar os dados do Supabase",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [toast]);

  // Funções utilitárias
  const toggleTripExpansion = (tripId: string) => {
    setExpandedTrips(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tripId)) {
        newSet.delete(tripId);
      } else {
        newSet.add(tripId);
      }
      return newSet;
    });
  };

  // Excluir viagem usando Supabase
  const handleDeleteTrip = async (id: string) => {
    if (!window.confirm("Tem certeza que deseja excluir esta viagem?")) {
      return;
    }
    
    try {
      setIsLoading(true);
      await tripService.deleteTrip(id);
      
      // Atualizar a lista local após excluir no Supabase
      setTrips(prevTrips => prevTrips.filter(trip => trip.id !== id));
      
      toast({
        title: "Sucesso",
        description: "Viagem removida com sucesso",
      });
    } catch (error) {
      console.error('Error deleting trip:', error);
      toast({
        title: "Erro",
        description: "Não foi possível remover a viagem",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Excluir cotação usando Supabase
  const handleDeleteQuotation = async (id: string) => {
    if (!window.confirm("Tem certeza que deseja excluir esta cotação?")) {
      return;
    }
    
    try {
      setIsLoading(true);
      await quotationService.deleteQuotation(id);
      
      // Atualizar a lista local após excluir no Supabase
      setQuotations(prevQuotations => prevQuotations.filter(quotation => quotation.id !== id));
      
      toast({
        title: "Sucesso",
        description: "Cotação removida com sucesso",
      });
    } catch (error) {
      console.error('Error deleting quotation:', error);
      toast({
        title: "Erro",
        description: "Não foi possível remover a cotação",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Associar cotação a uma viagem usando Supabase
  const associateQuotationToTrip = async (quotationId: string, tripId: string) => {
    try {
      setIsLoading(true);
      
      // Atualizar a cotação no Supabase
      await quotationService.updateQuotation(quotationId, {
        trip_id: tripId
      });
      
      // Atualizar a lista local
      setQuotations(prevQuotations => 
        prevQuotations.map(q => 
          q.id === quotationId 
            ? { ...q, tripId: tripId }
            : q
        )
      );
      
      toast({
        title: "Sucesso",
        description: "Cotação associada à viagem com sucesso",
      });
    } catch (error) {
      console.error('Error associating quotation to trip:', error);
      toast({
        title: "Erro",
        description: "Não foi possível associar a cotação à viagem",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Desassociar cotação de uma viagem usando Supabase
  const unassociateQuotationFromTrip = async (quotationId: string) => {
    try {
      setIsLoading(true);
      
      // Atualizar a cotação no Supabase, enviando undefined para trip_id
      await quotationService.updateQuotation(quotationId, {
        trip_id: undefined
      });
      
      // Atualizar a lista local
      setQuotations(prevQuotations => 
        prevQuotations.map(q => 
          q.id === quotationId 
            ? { ...q, tripId: undefined }
            : q
        )
      );
      
      toast({
        title: "Sucesso",
        description: "Cotação desassociada da viagem com sucesso",
      });
    } catch (error) {
      console.error('Error unassociating quotation from trip:', error);
      toast({
        title: "Erro",
        description: "Não foi possível desassociar a cotação da viagem",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Atualizar a função getFilteredTrips para incluir cotações
  const getFilteredTrips = () => {
    try {
      const filteredTrips = trips.filter(trip => {
        if (!trip || typeof trip !== 'object') return false;
        
        // Se não houver termo de busca, retorna true para incluir o item
        if (!searchTerm) return true;

        const searchTermLower = searchTerm.toLowerCase();
        
        // Busca por título
        const titleMatch = trip.title?.toLowerCase().includes(searchTermLower);
        
        // Busca por destinos
        const destinationMatch = Array.isArray(trip.destination)
          ? trip.destination.some(dest => dest.toLowerCase().includes(searchTermLower))
          : typeof trip.destination === 'string'
            ? trip.destination.toLowerCase().includes(searchTermLower)
            : false;
        
        // Busca por clientes
        const clientMatch = trip.clientIds?.some(clientId => {
          const clientName = getDisplayClientNames([clientId]);
          return clientName.toLowerCase().includes(searchTermLower);
        });

        const matchesSearch = titleMatch || destinationMatch || clientMatch;
        
        const matchesStatus = 
          statusFilter === 'all' || 
          trip.status === statusFilter;
        
        return matchesSearch && matchesStatus;
      });

      // Se o modo de visualização for 'quotations-only', retorna um array vazio
      if (viewMode === 'quotations-only') {
        return [];
      }

      return filteredTrips;
    } catch (error) {
      console.error('Error filtering trips:', error);
      return [];
    }
  };

  // Adicionar função para filtrar cotações
  const getFilteredQuotations = () => {
    try {
      return quotations.filter(quotation => {
        if (!quotation || typeof quotation !== 'object') return false;
        
        // Se não houver termo de busca, retorna true para incluir o item
        if (!searchTerm) return true;

        const searchTermLower = searchTerm.toLowerCase();
        
        // Busca por título
        const titleMatch = quotation.title?.toLowerCase().includes(searchTermLower);
        
        // Busca por destinos
        const destinationMatch = Array.isArray(quotation.destination)
          ? quotation.destination.some(dest => dest.toLowerCase().includes(searchTermLower))
          : typeof quotation.destination === 'string'
            ? quotation.destination.toLowerCase().includes(searchTermLower)
            : false;
        
        // Busca por cliente
        const clientMatch = quotation.client?.toLowerCase().includes(searchTermLower);

        const matchesSearch = titleMatch || destinationMatch || clientMatch;
        
        const matchesStatus = 
          statusFilter === 'all' || 
          quotation.status === statusFilter;
        
        return matchesSearch && matchesStatus;
      });
    } catch (error) {
      console.error('Error filtering quotations:', error);
      return [];
    }
  };

  // Funções para formatação de dados
  const formatTripStatus = (status: string) => {
    if (!status) return 'Status desconhecido';
    switch(status) {
      case 'quotation': return 'Cotação';
      case 'itinerary': return 'Roteiro';
      case 'day-by-day': return 'Day-by-Day';
      default: return status;
    }
  };

  const getTripStatusVariant = (status: string) => {
    if (!status) return { variant: 'secondary', className: '' };
    switch(status) {
      case 'quotation': return { variant: 'secondary', className: 'bg-yellow-200 text-yellow-800' };
      case 'itinerary': return { variant: 'default', className: 'bg-voah-teal' };
      case 'day-by-day': return { variant: 'outline', className: 'bg-voah-coral text-white' };
      default: return { variant: 'secondary', className: '' };
    }
  };

  const formatDateRange = (startDate: Date | string | null | undefined, endDate: Date | string | null | undefined) => {
    const formatBR = (date: string) => {
      if (!date) return '';
      // Se já estiver no formato YYYY-MM-DD
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        const [y, m, d] = date.split('-');
        return `${d}/${m}/${y}`;
      }
      // Se for string ISO
      if (typeof date === 'string' && date.includes('T')) {
        const [y, m, d] = date.split('T')[0].split('-');
        return `${d}/${m}/${y}`;
      }
      // Se for Date
      if (date instanceof Date) {
        return formatSafeDate(date, 'dd/MM/yyyy');
      }
      return '';
    };
    if (!startDate && !endDate) return 'Datas não definidas';
    if (startDate && endDate) {
      return `${formatBR(startDate as string)} - ${formatBR(endDate as string)}`;
    }
    if (startDate) return formatBR(startDate as string);
    if (endDate) return formatBR(endDate as string);
    return 'Datas não definidas';
  };

  const formatDestinations = (destinations: string[] | string | undefined) => {
    try {
      if (!destinations) return 'Sem destino';
      
      if (Array.isArray(destinations)) {
        if (destinations.length === 0) return 'Sem destino';
        return destinations.join(', ');
      }
      
      return String(destinations);
    } catch (error) {
      console.error('Error in formatDestinations:', error);
      return 'Sem destino';
    }
  };

  const formatClients = (clients: string[] | string | undefined) => {
    try {
      if (!clients) return 'Sem cliente';
      
      if (Array.isArray(clients)) {
        if (clients.length === 0) return 'Sem cliente';
        return clients.join(', ');
      }
      
      return String(clients);
    } catch (error) {
      console.error('Error in formatClients:', error);
      return 'Sem cliente';
    }
  };

  // Funções para formatação de dados de cotações
  const formatQuotationStatus = (status: string | undefined) => {
    if (!status) return 'Rascunho';
    switch(status.toLowerCase()) {
      case 'draft': return 'Rascunho';
      case 'pending': return 'Em análise';
      case 'approved': return 'Aprovada';
      case 'rejected': return 'Rejeitada';
      default: return status || 'Rascunho';
    }
  };

  const getQuotationStatusVariant = (status: string | undefined) => {
    if (!status) return { variant: 'secondary', className: '' };
    switch(status.toLowerCase()) {
      case 'approved': return { variant: 'default', className: 'bg-green-600' };
      case 'draft': return { variant: 'secondary', className: 'bg-gray-400' };
      case 'pending': return { variant: 'secondary', className: 'bg-yellow-500' };
      case 'rejected': return { variant: 'secondary', className: 'bg-red-500 text-white' };
      default: return { variant: 'secondary', className: '' };
    }
  };

  // Componentes visuais
  const filteredTrips = getFilteredTrips();

  const SafeViewModeDropdown = ({ 
    value, 
    onChange 
  }: { 
    value: 'all' | 'trips-only' | 'quotations-only', 
    onChange: (value: 'all' | 'trips-only' | 'quotations-only') => void 
  }) => {
    const handleSelect = (selectedValue: 'all' | 'trips-only' | 'quotations-only') => {
      if (!selectedValue || typeof selectedValue !== 'string') {
        console.error('Invalid view mode selected:', selectedValue);
        return;
      }
      
      onChange(selectedValue);
    };
    
    return (
      <Select value={value} onValueChange={handleSelect}>
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder="Mostrar todos" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Mostrar todos</SelectItem>
          <SelectItem value="trips-only">Apenas viagens</SelectItem>
          <SelectItem value="quotations-only">Apenas cotações</SelectItem>
        </SelectContent>
      </Select>
    );
  };

  // Funções para seleção de destino em viagens
  const handleAddTripDestination = (country: string) => {
    const destinations = Array.isArray(newTrip.destination) 
      ? newTrip.destination 
      : typeof newTrip.destination === 'string'
        ? [newTrip.destination]
        : [];
        
    if (!destinations.includes(country)) {
      const newDestinations = [...destinations, country];
      setNewTrip({...newTrip, destination: newDestinations});
    }
    setSearchDestination("");
  };

  const handleRemoveTripDestination = (country: string) => {
    const destinations = Array.isArray(newTrip.destination) 
      ? newTrip.destination 
      : typeof newTrip.destination === 'string'
        ? [newTrip.destination]
        : [];
        
    const newDestinations = destinations.filter(d => d !== country);
    setNewTrip({...newTrip, destination: newDestinations});
  };
  
  const filteredTripCountries = countries.filter(country => {
    const destinations = Array.isArray(newTrip.destination) 
      ? newTrip.destination 
      : typeof newTrip.destination === 'string'
        ? [newTrip.destination]
        : [];
        
    return country.toLowerCase().includes(searchDestination.toLowerCase()) && 
      !destinations.includes(country);
  });
  
  // Funções para seleção de destino em itinerários
  const handleAddItineraryDestination = (country: string) => {
    const destinations = Array.isArray(newTrip.destination) 
      ? newTrip.destination 
      : typeof newTrip.destination === 'string'
        ? [newTrip.destination]
        : [];
        
    if (!destinations.includes(country)) {
      const newDestinations = [...destinations, country];
      setNewTrip({...newTrip, destination: newDestinations});
    }
    setSearchDestination("");
  };

  const handleRemoveItineraryDestination = (country: string) => {
    const destinations = Array.isArray(newTrip.destination) 
      ? newTrip.destination 
      : typeof newTrip.destination === 'string'
        ? [newTrip.destination]
        : [];
        
    const newDestinations = destinations.filter(d => d !== country);
    setNewTrip({...newTrip, destination: newDestinations});
  };
  
  const filteredItineraryCountries = countries.filter(country => {
    const destinations = Array.isArray(newTrip.destination) 
      ? newTrip.destination 
      : typeof newTrip.destination === 'string'
        ? [newTrip.destination]
        : [];
        
    return country.toLowerCase().includes(searchDestination.toLowerCase()) && 
      !destinations.includes(country);
  });
  
  // Adicionar nova função para criar itinerário
  const handleCreateItinerary = async () => {
    if (!newTrip.title) {
      toast({
        title: "Erro",
        description: "O título do itinerário é obrigatório",
        variant: "destructive",
      });
      return;
    }

    if (newTrip.clientIds?.length === 0) {
      toast({
        title: "Erro",
        description: "O cliente é obrigatório",
        variant: "destructive",
      });
      return;
    }

    if (!newTrip.destination || (Array.isArray(newTrip.destination) && newTrip.destination.length === 0)) {
      toast({
        title: "Erro",
        description: "O destino é obrigatório",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);
      
      // Preparar dados para o formato do Supabase
      const tripData = {
        title: newTrip.title || '',
        client_ids: newTrip.clientIds || [],
        destination: newTrip.destination,
        status: 'quotation' as 'quotation' | 'itinerary' | 'day-by-day',
        days: [],
        itinerary_type: 'detailed'
      };

      // Criar itinerário no Supabase
      const createdTrip = await tripService.createTrip(tripData);
      
      // Converter formato para o componente
      const formattedTrip: Trip = {
        id: createdTrip.id,
        title: createdTrip.title,
        clientIds: createdTrip.client_ids || [],
        destination: createdTrip.destination,
        startDate: null,
        endDate: null,
        status: createdTrip.status,
        days: createdTrip.days,
        itineraryType: createdTrip.itinerary_type
      };
      
      // Atualizar estado local
      setTrips([formattedTrip, ...trips]);
      
      // Limpar o formulário
      setNewTrip({
        title: '',
        clientIds: [],
        destination: [],
        status: 'quotation',
      });
      
      toast({
        title: "Sucesso",
        description: "Itinerário criado com sucesso",
      });
    } catch (error) {
      console.error('Error creating itinerary:', error);
      toast({
        title: "Erro",
        description: "Não foi possível criar o itinerário",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Adicionar função para criar viagem
  const handleCreateTrip = async () => {
    try {
      if (!newTrip.clientIds || newTrip.clientIds.length === 0) {
        toast({
          title: "Cliente obrigatório",
          description: "Selecione pelo menos um cliente para a viagem.",
          variant: "destructive"
        });
        return;
      }
      
      if (!newTrip.destination || (Array.isArray(newTrip.destination) && newTrip.destination.length === 0)) {
        toast({
          title: "Destino obrigatório",
          description: "Selecione pelo menos um destino para a viagem.",
          variant: "destructive"
        });
        return;
      }
      
      // Preparar dados para o formato do Supabase
      const tripData = {
        title: newTrip.title || '',
        client_ids: newTrip.clientIds,
        destination: newTrip.destination,
        start_date: newTrip.startDate ? new Date(newTrip.startDate).toISOString() : null,
        end_date: newTrip.endDate ? new Date(newTrip.endDate).toISOString() : null,
        status: newTrip.status || 'quotation',
        notes: newTrip.notes || '',
        cover_images: newTrip.coverImages || [],
        days: newTrip.days || [],
        itinerary_type: newTrip.itineraryType || '',
      };

      setIsLoading(true);
      
      // Criar viagem no Supabase
      const createdTrip = await tripService.createTrip(tripData);
      
      // Converter formato de volta para o componente
      const formattedTrip: Trip = {
        id: createdTrip.id,
        title: createdTrip.title,
        clientIds: createdTrip.client_ids || [],
        destination: createdTrip.destination,
        startDate: createdTrip.start_date ? new Date(createdTrip.start_date) : null,
        endDate: createdTrip.end_date ? new Date(createdTrip.end_date) : null,
        status: createdTrip.status,
        notes: createdTrip.notes,
        coverImages: createdTrip.cover_images,
        days: createdTrip.days,
        itineraryType: createdTrip.itinerary_type
      };
      
      // Atualizar estado local
      setTrips([formattedTrip, ...trips]);
      
      // Limpar o formulário
      setNewTrip({
        destination: [],
        clientIds: [],
        status: 'quotation',
      });
      setShowTripForm(false);
      setSelectedClients([]);
      
      toast({
        title: "Sucesso",
        description: "Viagem criada com sucesso",
      });
    } catch (error) {
      console.error('Error creating trip:', error);
      toast({
        title: "Erro",
        description: "Não foi possível criar a viagem",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDuplicateTrip = async (id: string) => {
    try {
      setIsLoading(true);
      
      // Buscar a viagem original pelo ID
      const originalTrip = await tripService.getTripById(id);
      
      if (!originalTrip) {
      toast({
        title: "Erro",
          description: "Viagem não encontrada",
          variant: "destructive",
        });
        return;
      }
      
      // Criar cópia sem ID e timestamps
      const { id: _, created_at, updated_at, ...tripCopy } = originalTrip;
      
      // Modificar título para indicar que é uma cópia
      tripCopy.title = `Cópia de ${tripCopy.title || 'Viagem sem título'}`;
      
      // Garantir que itinerary_type seja string e não null
      if (tripCopy.itinerary_type === null) {
        tripCopy.itinerary_type = '';
      }
      
      // Criar nova viagem no Supabase
      const createdTrip = await tripService.createTrip(tripCopy);
      
      // Converter para o formato do componente
      const formattedTrip: Trip = {
        id: createdTrip.id,
        title: createdTrip.title,
        clientIds: createdTrip.client_ids || [],
        destination: createdTrip.destination,
        startDate: createdTrip.start_date ? new Date(createdTrip.start_date) : null,
        endDate: createdTrip.end_date ? new Date(createdTrip.end_date) : null,
        status: createdTrip.status,
        notes: createdTrip.notes,
        coverImages: createdTrip.cover_images,
        days: createdTrip.days,
        itineraryType: createdTrip.itinerary_type
      };
      
      // Atualizar estado local
      setTrips([formattedTrip, ...trips]);
      
      toast({
        title: "Sucesso",
        description: "Viagem duplicada com sucesso",
      });
    } catch (error) {
      console.error('Error duplicating trip:', error);
      toast({
        title: "Erro",
        description: "Não foi possível duplicar a viagem",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDuplicateQuotation = async (id: string) => {
    try {
      setIsLoading(true);
      
      // Buscar a cotação original pelo ID
      const originalQuotation = await quotationService.getQuotationById(id);
      
      if (!originalQuotation) {
        toast({
          title: "Erro",
          description: "Cotação não encontrada",
          variant: "destructive",
        });
        return;
      }
      
      // Criar cópia sem ID e timestamps
      const { id: _, created_at, updated_at, ...quotationCopy } = originalQuotation;
      
      // Modificar título para indicar que é uma cópia
      quotationCopy.title = `Cópia de ${quotationCopy.title || 'Cotação sem título'}`;
      
      // Criar nova cotação no Supabase
      const createdQuotation = await quotationService.createQuotation(quotationCopy);
      
      // Converter para o formato do componente, mantendo todas as propriedades
      const formattedQuotation: Quotation = {
        id: createdQuotation.id,
        title: createdQuotation.title,
        client: createdQuotation.client || '',
        destination: createdQuotation.destination,
        startDate: createdQuotation.start_date ? new Date(createdQuotation.start_date) : null,
        endDate: createdQuotation.end_date ? new Date(createdQuotation.end_date) : null,
        status: 'quotation',
        tripId: createdQuotation.trip_id,
        // Manter todas as configurações de visibilidade e outras propriedades
        showRecommendationsTab: createdQuotation.show_recommendations_tab,
        showPriceTab: createdQuotation.show_price_tab,
        showAccommodationsTab: createdQuotation.show_accommodations_tab,
        accommodationsTabName: createdQuotation.accommodations_tab_name,
        showFlightsTab: createdQuotation.show_flights_tab,
        flightsTabName: createdQuotation.flights_tab_name,
        priceTabName: createdQuotation.price_tab_name,
        recommendationsTabName: createdQuotation.recommendations_tab_name,
        useDayMode: createdQuotation.use_day_mode,
        usePageDropdown: createdQuotation.use_page_dropdown,
        showMapLines: createdQuotation.show_map_lines,
        showAccommodationsInItinerary: createdQuotation.show_accommodations_in_itinerary,
        showFlightsInItinerary: createdQuotation.show_flights_in_itinerary,
        useCustomAccommodationGroups: createdQuotation.use_custom_accommodation_groups,
        accommodationsDescription: createdQuotation.accommodations_description,
        useCustomFlightGroups: createdQuotation.use_custom_flight_groups,
        flightsDescription: createdQuotation.flights_description,
        showAccommodationsMapSection: createdQuotation.show_accommodations_map_section,
        // Grupos e listas complexas
        accommodationGroups: createdQuotation.accommodation_groups,
        flightGroups: createdQuotation.flight_groups,
        recommendationGroups: createdQuotation.recommendation_groups
      };
      
      // Atualizar estado local
      setQuotations([formattedQuotation, ...quotations]);
      
      toast({
        title: "Sucesso",
        description: "Cotação duplicada com sucesso",
      });
    } catch (error) {
      console.error('Error duplicating quotation:', error);
      toast({
        title: "Erro",
        description: "Não foi possível duplicar a cotação",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Modificar a função para abrir o diálogo de versões em vez de publicar diretamente
  const handlePublishQuotation = async (id: string) => {
    setSelectedQuotationId(id);
    
    try {
      setIsLoading(true);
      
      // Buscar versões da cotação
      const versions = await quotationVersioningService.getQuotationVersions(id);
      
      // Atualizar estados com as versões
      setWorkVersion(versions.workVersion);
      setPublishedVersion(versions.publishedVersion);
      setHistoryVersion(versions.historyVersion);
      
      // Abrir o diálogo
      setShowVersionsDialog(true);
    } catch (error) {
      console.error('Error loading quotation versions:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as versões da cotação",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Adicionar função para publicar a cotação a partir do diálogo
  const publishSelectedQuotation = async () => {
    if (!selectedQuotationId) return;
    
    try {
      setIsLoading(true);
      
      // Publicar a cotação usando o serviço de versionamento
      const published = await quotationVersioningService.publishQuotation(selectedQuotationId);
      
      if (published) {
        toast({
          title: "Cotação publicada",
          description: "A cotação foi publicada com sucesso",
        });
        
        // Fechar o diálogo
        setShowVersionsDialog(false);
        
        // Atualizar as versões
        const versions = await quotationVersioningService.getQuotationVersions(selectedQuotationId);
        setWorkVersion(versions.workVersion);
        setPublishedVersion(versions.publishedVersion);
        setHistoryVersion(versions.historyVersion);
      }
    } catch (error) {
      console.error('Error publishing quotation:', error);
      toast({
        title: "Erro",
        description: "Não foi possível publicar a cotação",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Função para formatar data
  const formatVersionDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (e) {
      return 'Data inválida';
    }
  };
  
  // Função para copiar link
  const copyVersionLink = (versionId: string) => {
    if (!versionId) return;
    
    // Construir a URL
    const quotationViewUrl = `${window.location.origin}/quotation/${versionId}`;
    
    // Copiar para o clipboard
    navigator.clipboard.writeText(quotationViewUrl)
      .then(() => {
        setCopiedVersionId(versionId);
        
        toast({
          title: "Link copiado",
          description: "O link da cotação foi copiado para a área de transferência",
        });
        
        // Reset após 2 segundos
        setTimeout(() => {
          setCopiedVersionId(null);
        }, 2000);
      })
      .catch(() => {
        toast({
          title: "Erro ao copiar link",
          description: "Não foi possível copiar o link automaticamente",
          variant: "destructive",
        });
      });
  };

  const SafeSelectTripQuotationDropdown = ({ onTripSelect, quotationId }: { onTripSelect: (tripId: string) => void, quotationId: string }) => {
    const [selectedTrip, setSelectedTrip] = useState<string | null>(null);
    
    const handleTripSelect = (tripId: string) => {
      if (!tripId || typeof tripId !== 'string') {
        console.error('Invalid trip ID selected:', tripId);
        return;
      }
      
      setSelectedTrip(tripId);
      onTripSelect(tripId);
    };
    
    const validTrips = trips.filter(trip => trip && typeof trip === 'object' && trip.id);
    
    return (
      <div className="flex items-center space-x-2">
        <select 
          className="p-2 border rounded text-sm"
          value={selectedTrip || ''}
          onChange={(e) => handleTripSelect(e.target.value)}
        >
          <option value="">Selecione uma viagem...</option>
          {validTrips.map(trip => (
            <option key={trip.id} value={trip.id}>
              {trip.clientIds.join(', ')} - {Array.isArray(trip.destination) ? trip.destination.join(', ') : trip.destination || 'Destino não definido'}
            </option>
          ))}
        </select>
        <Button 
          type="button" 
          variant="outline"
          size="sm"
          onClick={() => selectedTrip && associateQuotationToTrip(quotationId, selectedTrip)}
          disabled={!selectedTrip}
          className="text-xs h-8"
        >
          <LinkIcon className="h-3.5 w-3.5 mr-1" />
          Associar
        </Button>
      </div>
    );
  };

  // Adicionar funções auxiliares antes do componente TravelHub
  const getItinerariesForTrip = async (tripId: string) => {
    try {
      // Buscar todas as viagens com o mesmo tripId
      const tripsData = await tripService.getTrips();
      return tripsData.filter(trip => trip.id === tripId);
    } catch (error) {
      console.error('Error getting itineraries for trip:', error);
      return [];
    }
  };

  const getQuotationsForTrip = async (tripId: string) => {
    try {
      // Buscar cotações pelo trip_id
      const quotations = await quotationService.getQuotationsByTrip(tripId);
      return quotations;
    } catch (error) {
      console.error('Error getting quotations for trip:', error);
      return [];
    }
  };

  const getItineraryStatusVariant = (status: string) => {
    if (!status) return { variant: 'secondary', className: '' };
    switch(status) {
      case 'draft': return { variant: 'secondary', className: 'bg-gray-400' };
      case 'active': return { variant: 'default', className: 'bg-voah-teal' };
      case 'pending': return { variant: 'secondary', className: 'bg-yellow-500' };
      case 'completed': return { variant: 'secondary', className: 'bg-green-500' };
      default: return { variant: 'secondary', className: '' };
    }
  };

  const formatItineraryStatus = (status: string) => {
    if (!status) return 'Status desconhecido';
    switch(status) {
      case 'draft': return 'Rascunho';
      case 'active': return 'Ativo';
      case 'pending': return 'Pendente';
      case 'completed': return 'Concluído';
      default: return status;
    }
  };

  const unassociateItineraryFromTrip = async (itineraryId: string) => {
    try {
      // Buscar o itinerário pelo ID
      const trip = await tripService.getTripById(itineraryId);
      
      if (trip) {
        // Remover a associação com a viagem
        const updatedTrip = {
          ...trip,
          trip_id: null  // Remover a referência à viagem
        };
        
        // Atualizar no Supabase
        await tripService.updateTrip(itineraryId, updatedTrip);
        
        // Atualizar o estado local
        setTrips(prevTrips => prevTrips.map(t => 
          t.id === itineraryId ? { ...t, tripId: undefined } : t
        ));
      }
    } catch (error) {
      console.error('Error unassociating itinerary from trip:', error);
      toast({
        title: "Erro",
        description: "Não foi possível desassociar o itinerário da viagem",
        variant: "destructive",
      });
    }
  };

  const handleClientSearch = async (value: string) => {
    setClientSearch(value);
    
    if (!value.trim()) {
      setAvailableClients([]);
      return;
    }
    
    try {
      // Buscar clientes pelo termo de busca
      const clients = await clientService.searchClients(value);
      
      // Filtrar os clientes que já foram selecionados
      const selectedIds = selectedClients.map(c => c.id);
      const filteredClients = clients.filter(c => !selectedIds.includes(c.id));
      
      setAvailableClients(filteredClients);
    } catch (error) {
      console.error('Error searching clients:', error);
      setAvailableClients([]);
    }
  };

  const handleAddClient = (client: Client) => {
    // Adicionar cliente à seleção com o objeto completo do cliente
    setSelectedClients([...selectedClients, client]);
      
    // Atualizar os IDs de clientes no objeto da viagem
    setNewTrip({
      ...newTrip,
      clientIds: [...(newTrip.clientIds || []), client.id]
    });
      
    // Limpar a busca e os resultados
      setClientSearch('');
      setAvailableClients([]);
  };

  const handleRemoveClient = async (clientId: string) => {
    // Remover cliente da seleção
    setSelectedClients(selectedClients.filter(c => c.id !== clientId));
      
    // Atualizar os IDs de clientes no objeto da viagem
    setNewTrip({
      ...newTrip,
      clientIds: (newTrip.clientIds || []).filter(id => id !== clientId)
    });
  };
  
  // Carregar clientes selecionados quando o formulário é aberto ou quando os clientIds mudam
  useEffect(() => {
    const loadSelectedClients = async () => {
      if (showTripForm && newTrip.clientIds?.length) {
        try {
          // Buscar clientes diretamente do Supabase para cada ID
          const clientPromises = newTrip.clientIds.map(id => 
            clientService.getClientById(id)
          );
          
          const clientsData = await Promise.all(clientPromises);
          // Filtrar resultados nulos
          const validClients = clientsData.filter(c => c !== null) as Client[];
          
          setSelectedClients(validClients);
      } catch (error) {
        console.error('Error loading selected clients:', error);
          setSelectedClients([]);
      }
      } else {
        setSelectedClients([]);
    }
    };
    
    loadSelectedClients();
  }, [showTripForm, newTrip.clientIds]);

  return (
    <AdminLayout>
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h1 className="text-2xl font-bold text-gray-800">Gerenciador de Viagens</h1>
          <div className="flex gap-2">
            <SafeViewModeDropdown
              value={viewMode || 'all'}
              onChange={(value) => setViewMode(value)}
            />
            <Button 
              onClick={() => {
                setShowTripForm(true);
              }}
              className="bg-voah-teal hover:bg-voah-teal-dark"
            >
              <Plus className="mr-2 h-4 w-4" />
              Nova Viagem
            </Button>
            <Button 
              onClick={async () => {
                try {
                  setIsLoading(true);
                  
                  // Criar nova cotação usando o serviço do Supabase
                  const newQuotation = await quotationService.createQuotation({
                  title: "Nova Cotação",
                  client: "",
                  destination: [],
                    start_date: null,
                    end_date: null,
                    pages: [],
                    show_price_tab: false,
                    show_recommendations_tab: false
                  });
                  
                  // Converter formato para o componente
                  const formattedQuotation: Quotation = {
                    id: newQuotation.id,
                    title: newQuotation.title,
                    client: newQuotation.client || '',
                    destination: newQuotation.destination,
                  startDate: null,
                  endDate: null,
                    status: 'quotation',
                    showPriceTab: false,
                    showRecommendationsTab: false
                  };
                
                // Atualizar o estado local
                  setQuotations([formattedQuotation, ...quotations]);
                
                // Mostrar mensagem de sucesso
                toast({
                  title: "Sucesso",
                  description: "Nova cotação criada com sucesso",
                });
                
                // Navegar para a página de edição de cotação
                  navigate(`/admin/quotations/${newQuotation.id}`);
                } catch (error) {
                  console.error('Error creating quotation:', error);
                  toast({
                    title: "Erro",
                    description: "Não foi possível criar a cotação",
                    variant: "destructive",
                  });
                } finally {
                  setIsLoading(false);
                }
              }}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={isLoading}
            >
              <Plus className="mr-2 h-4 w-4" />
              Nova Cotação
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-100">
          <div className="flex flex-col md:flex-row gap-3 p-3">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                type="search"
                placeholder="Buscar por cliente, destino ou título..."
                className="pl-8 w-full"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Todos os status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  <SelectItem value="quotation">Cotação</SelectItem>
                  <SelectItem value="draft">Rascunho</SelectItem>
                  <SelectItem value="active">Ativo</SelectItem>
                  <SelectItem value="pending">Pendente</SelectItem>
                  <SelectItem value="completed">Concluído</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon">
                <Filter className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {showTripForm && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Nova Viagem</h3>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => {
                    // Ocultar o formulário
                    setShowTripForm(false);
                    // Limpar o formulário
                    setNewTrip({
                      destination: [],
                      clientIds: [],
                      status: 'quotation',
                    });
                    // Limpar os estados relacionados ao formulário
                    setClientSearch('');
                    setAvailableClients([]);
                    setSelectedClients([]);
                    setSearchDestination('');
                  }}
                >
                  Cancelar
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Cliente</label>
                  <div className="relative">
                    <Input 
                      placeholder="Buscar cliente..." 
                      value={clientSearch || ''}
                      onChange={(e) => handleClientSearch(e.target.value)}
                      className="w-full"
                    />
                    {availableClients.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                        {availableClients.map((client) => (
                          <div
                            key={client.id}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => handleAddClient(client)}
                          >
                            {client.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {selectedClients.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedClients.map((client) => (
                        <Badge key={client.id} variant="secondary" className="flex items-center gap-1">
                          {client.name}
                          <button
                            type="button"
                            className="rounded-full ml-1 hover:bg-background/80"
                            onClick={() => handleRemoveClient(client.id)}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Destino</label>
                  <Popover open={destinationInputOpen} onOpenChange={setDestinationInputOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={destinationInputOpen}
                        className="w-full justify-between text-left"
                      >
                        {Array.isArray(newTrip.destination) && newTrip.destination.length > 0 
                          ? `${newTrip.destination.length} países selecionados` 
                          : "Selecione os países"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0" align="start">
                      <Command>
                        <CommandInput 
                          placeholder="Buscar país..." 
                          value={searchDestination}
                          onValueChange={setSearchDestination}
                        />
                        <CommandList>
                          <CommandEmpty>Nenhum país encontrado.</CommandEmpty>
                          <CommandGroup>
                            {filteredTripCountries.map((country) => (
                              <CommandItem
                                key={country}
                                onSelect={() => handleAddTripDestination(country)}
                              >
                                {country}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  
                  {Array.isArray(newTrip.destination) && newTrip.destination.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {newTrip.destination.map(country => (
                        <Badge key={country} variant="secondary" className="flex items-center gap-1">
                          {country}
                          <button
                            type="button"
                            className="rounded-full ml-1 hover:bg-background/80"
                            onClick={() => handleRemoveTripDestination(country)}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Data de início</label>
                  <Input 
                    type="date"
                    onChange={(e) => {
                      const date = new Date(e.target.value);
                      setNewTrip({...newTrip, startDate: date});
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Data de fim</label>
                  <Input 
                    type="date"
                    onChange={(e) => {
                      const date = new Date(e.target.value);
                      setNewTrip({...newTrip, endDate: date});
                    }}
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Observações</label>
                <Textarea 
                  placeholder="Observações sobre a viagem" 
                  value={newTrip.notes || ''} 
                  onChange={(e) => setNewTrip({...newTrip, notes: e.target.value})}
                  rows={3}
                />
              </div>
              <div className="flex justify-end">
                <Button 
                  onClick={handleCreateTrip}
                  className="bg-voah-teal hover:bg-voah-teal-dark"
                >
                  Criar Viagem
                </Button>
              </div>
            </div>
          )}

          <ErrorBoundary>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Cliente</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Destino</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600 hidden sm:table-cell">Data</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Status</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-600">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredTrips().map((trip) => (
                    <React.Fragment key={trip.id}>
                      <tr className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 text-gray-800">
                          <div className="flex items-center">
                            <button 
                              onClick={() => toggleTripExpansion(trip.id)}
                              className="mr-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                            >
                              {expandedTrips.has(trip.id) ? 
                                <ChevronDown className="h-5 w-5" /> : 
                                <ChevronRight className="h-5 w-5" />
                              }
                            </button>
                            <div className="font-medium">{getDisplayClientNames(trip.clientIds)}</div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-800">{formatDestinations(trip.destination)}</td>
                        <td className="py-3 px-4 text-gray-600 hidden sm:table-cell">
                          {formatDateRange(trip.startDate, trip.endDate)}
                        </td>
                        <td className="py-3 px-4">
                          <Badge 
                            variant={getTripStatusVariant(trip.status).variant as any}
                            className={getTripStatusVariant(trip.status).className}
                          >
                            {formatTripStatus(trip.status)}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex justify-end items-center gap-2">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              title="Editar"
                              onClick={() => navigate(`/admin/trips/${trip.id}`)}
                            >
                              <Edit className="h-4 w-4 text-gray-600" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              title="Duplicar"
                              onClick={() => handleDuplicateTrip(trip.id)}
                            >
                              <FolderPlus className="h-4 w-4 text-gray-600" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              title="Excluir"
                              onClick={() => handleDeleteTrip(trip.id)}
                            >
                              <Trash className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      
                      {/* Cotações associadas a esta viagem (visíveis quando expandidos) */}
                      {expandedTrips.has(trip.id) && getFilteredQuotations().filter(quotation => quotation.tripId === trip.id).map((quotation: Quotation) => (
                        <tr key={quotation.id} className="bg-gray-50 hover:bg-gray-100 border-b border-gray-100">
                          <td className="py-3 px-4 text-gray-700">
                            <div className="flex items-center pl-7">
                              <div className="ml-2 text-sm">{quotation.title || 'Sem título'} <span className="text-xs font-medium bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded-sm">Cotação</span></div>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-gray-700 text-sm">
                            {formatDestinations(quotation.destination)}
                          </td>
                          <td className="py-3 px-4 text-gray-700 text-sm hidden sm:table-cell">
                            {formatDateRange(quotation.startDate, quotation.endDate)}
                          </td>
                          <td className="py-3 px-4">
                            <Badge 
                              variant={getQuotationStatusVariant(quotation.status).variant as any}
                              className={getQuotationStatusVariant(quotation.status).className}
                            >
                              {formatQuotationStatus(quotation.status)}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex justify-end items-center gap-2">
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                title="Visualizar"
                                onClick={() => {
                                  // Remover qualquer rascunho existente para garantir que a cotação real seja exibida
                                  localStorage.removeItem('voah_quotation_draft');
                                  // Navegar para a página de visualização da cotação
                                  navigate(`/quotation/${quotation.id}`);
                                }}
                              >
                                <Eye className="h-4 w-4 text-gray-600" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                title="Editar"
                                onClick={() => {
                                  // Limpar o rascunho antes de navegar
                                  localStorage.removeItem('voah_quotation_draft');
                                  navigate(`/admin/quotations/${quotation.id}`);
                                }}
                              >
                                <Edit className="h-4 w-4 text-gray-600" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                title="Duplicar"
                                onClick={() => handleDuplicateQuotation(quotation.id)}
                              >
                                <FolderPlus className="h-4 w-4 text-gray-600" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                title="Publicar"
                                onClick={() => handlePublishQuotation(quotation.id)}
                                className="text-voah-coral hover:text-voah-coral/90"
                              >
                                <Send className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                title="Excluir"
                                onClick={() => handleDeleteQuotation(quotation.id)}
                              >
                                <Trash className="h-4 w-4 text-red-600" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  
                  {getFilteredTrips().length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-gray-500">
                        {searchTerm || statusFilter !== 'all' 
                          ? 'Nenhuma viagem encontrada com os filtros aplicados.' 
                          : 'Nenhuma viagem cadastrada. Clique em "Nova Viagem" para começar.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </ErrorBoundary>
        </div>
      </div>
      
      {/* Diálogo de versões */}
      <Dialog open={showVersionsDialog} onOpenChange={setShowVersionsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Versões da Cotação</DialogTitle>
            <DialogDescription>
              Visualize e gerencie as versões desta cotação
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 my-2">
            {/* Versão de trabalho */}
            <div className="border rounded-md p-3">
              <div className="font-medium">Versão de trabalho</div>
              <div className="text-sm text-gray-500 mb-2">
                {workVersion?.last_updated_at 
                  ? `Última atualização: ${formatVersionDate(workVersion.last_updated_at)}` 
                  : 'Nova versão'}
              </div>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    if (selectedQuotationId) {
                      setShowVersionsDialog(false);
                      navigate(`/admin/quotations/${selectedQuotationId}`);
                    }
                  }}
                >
                  <Edit className="h-3.5 w-3.5 mr-1" />
                  Editar
                </Button>
                <Button 
                  size="sm" 
                  variant="default" 
                  className="flex-1 bg-voah-coral hover:bg-voah-coral/90"
                  onClick={publishSelectedQuotation}
                >
                  <Send className="h-3.5 w-3.5 mr-1" />
                  Publicar
                </Button>
              </div>
            </div>
            
            {/* Versão publicada */}
            {publishedVersion && (
              <div className="border rounded-md p-3">
                <div className="font-medium">Versão publicada</div>
                <div className="text-sm text-gray-500 mb-2">
                  Publicada em: {formatVersionDate(publishedVersion.published_at)}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      if (publishedVersion.id) {
                        window.open(`/quotation/${publishedVersion.id}`, '_blank');
                      }
                    }}
                  >
                    <Eye className="h-3.5 w-3.5 mr-1" />
                    Visualizar
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => copyVersionLink(publishedVersion.id)}
                  >
                    <Copy className="h-3.5 w-3.5 mr-1" />
                    Copiar Link
                  </Button>
                </div>
              </div>
            )}
            
            {/* Versão histórica */}
            {historyVersion && (
              <div className="border rounded-md p-3">
                <div className="font-medium">Versão histórica</div>
                <div className="text-sm text-gray-500 mb-2">
                  Publicada em: {formatVersionDate(historyVersion.published_at)}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      if (historyVersion.id) {
                        window.open(`/quotation/${historyVersion.id}`, '_blank');
                      }
                    }}
                  >
                    <Eye className="h-3.5 w-3.5 mr-1" />
                    Visualizar
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => copyVersionLink(historyVersion.id)}
                  >
                    <Copy className="h-3.5 w-3.5 mr-1" />
                    Copiar Link
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
} 